const hasOwn = (object, property) =>
  Object.prototype.hasOwnProperty.call(object, property)

// Promise middleware may resolve with either the validation response itself or
// an action envelope. An explicitly-null data property is the legacy clean
// response and must not fall through to the envelope metadata.
export const getParValidationResponseData = (result) =>
  result != null && typeof result === 'object' && hasOwn(result, 'data')
    ? result.data
    : result

const normalizeModule = (module) =>
  String(module || '')
    .trim()
    .toLowerCase()

const isErrorMessage = (message) =>
  typeof message === 'string'
    ? message.trim().length > 0
    : Array.isArray(message) &&
      message.length > 0 &&
      message.every(
        (item) => typeof item === 'string' && item.trim().length > 0
      )

const blockedValidation = (entries = []) => ({
  isClean: false,
  canDownload: false,
  entries,
  noErrorModules: [],
})

// /par/validate is a legacy dictionary keyed by installed module ID. It does
// not describe generated output files; only a null or empty dictionary is clean.
export const getParValidationState = (
  validationData,
  installedModules = []
) => {
  if (validationData == null) {
    return {
      isClean: true,
      canDownload: false,
      entries: [],
      noErrorModules: [],
    }
  }

  if (
    typeof validationData !== 'object' ||
    Array.isArray(validationData) ||
    Object.getPrototypeOf(validationData) !== Object.prototype
  ) {
    return blockedValidation()
  }

  const keys = Object.keys(validationData)
  if (keys.length === 0) {
    return {
      isClean: true,
      canDownload: false,
      entries: [],
      noErrorModules: [],
    }
  }

  if (
    hasOwn(validationData, 'hasParErrors') &&
    typeof validationData.hasParErrors !== 'boolean'
  ) {
    return blockedValidation()
  }

  const errorKeys = keys.filter((key) => key !== 'hasParErrors')
  if (hasOwn(validationData, 'error')) {
    return blockedValidation(
      errorKeys.map((key) => ({ label: key, message: validationData[key] }))
    )
  }
  if (validationData.hasParErrors === true && errorKeys.length === 0) {
    return blockedValidation()
  }

  const installedById = installedModules.reduce((modules, module) => {
    const id = normalizeModule(module)
    return id ? { ...modules, [id]: module } : modules
  }, {})

  const entries = errorKeys.map((key) => ({
    label: installedById[normalizeModule(key)] || key,
    message: validationData[key],
  }))
  const hasUnknownModule = errorKeys.some(
    (key) => !installedById[normalizeModule(key)]
  )
  const hasMalformedMessage = entries.some(
    (entry) => !isErrorMessage(entry.message)
  )

  if (hasUnknownModule || hasMalformedMessage) return blockedValidation(entries)

  if (errorKeys.length === 0) {
    return {
      isClean: true,
      canDownload: false,
      entries: [],
      noErrorModules: [],
    }
  }

  const errorModuleIds = errorKeys.map(normalizeModule)
  const noErrorModules = Object.keys(installedById)
    .filter((id) => !errorModuleIds.includes(id))
    .map((id) => installedById[id])

  return {
    isClean: false,
    canDownload: noErrorModules.length > 0,
    entries,
    noErrorModules,
  }
}
